import React, { useState } from 'react';
import { Box, Paper, Typography, Tooltip, Stack } from '@mui/material';

/**
 * Componente SVG para visualización de la Curva de Eficacia Terapéutica.
 * Renderiza gráfico de dispersión (scatter plot) + línea de tendencia por regresión lineal.
 *
 * @param {Array} puntos - Array de objetos: [{ sesionNum, fecha, puntaje, porcentaje, porcentajeTendencia }]
 * @param {Object} regresion - Objeto { pendiente, intercepto, r2, proyeccion100 }
 * @param {string} titulo - Título del gráfico
 * @param {string} subtitulo - Subtítulo descriptivo
 */
const GraficoCurvaEficacia = ({ puntos = [], regresion = {}, titulo = '', subtitulo = '' }) => {
    const [puntoActivo, setPuntoActivo] = useState(null);

    if (!puntos || puntos.length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: '#fafafa' }}>
                <Typography variant="body2" color="text.secondary">
                    Seleccione un plan de terapia con sesiones evaluadas para generar la curva de eficacia.
                </Typography>
            </Paper>
        );
    }

    // Dimensiones del gráfico SVG
    const svgWidth = 800;
    const svgHeight = 360;
    const padLeft = 65;
    const padRight = 45;
    const padTop = 30;
    const padBottom = 55;

    const plotWidth = svgWidth - padLeft - padRight;
    const plotHeight = svgHeight - padTop - padBottom;

    // Escala Eje X: de 1 hasta max(10, N + 2 para proyección)
    const maxSesionNum = Math.max(...puntos.map((p) => p.sesionNum), 1);
    const xMax = Math.max(maxSesionNum + 2, 8);
    const xMin = 1;

    // Funciones de mapeo de coordenadas
    const getX = (sesionNum) => padLeft + ((sesionNum - xMin) / (xMax - xMin)) * plotWidth;
    const getY = (porcentaje) => {
        const p = Math.max(0, Math.min(100, porcentaje));
        return padTop + plotHeight - (p / 100) * plotHeight;
    };

    // Puntos de la línea de tendencia (de x=1 a x=maxSesionNum o xMax)
    const tendStart = {
        x: getX(1),
        y: getY(regresion.pendiente ? 1 * regresion.pendiente + regresion.intercepto : 0),
    };
    const tendEnd = {
        x: getX(maxSesionNum),
        y: getY(regresion.pendiente ? maxSesionNum * regresion.pendiente + regresion.intercepto : 0),
    };

    // Marcas de cuadrícula Eje Y (0%, 25%, 50%, 75%, 100%)
    const marcasY = [0, 25, 50, 75, 100];

    // Marcas de cuadrícula Eje X
    const marcasX = [];
    const pasoX = xMax > 15 ? 2 : 1;
    for (let i = 1; i <= xMax; i += pasoX) {
        marcasX.push(i);
    }

    // Ruta de polilínea para la curva real
    const dCurvaReal = puntos.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${getX(p.sesionNum)} ${getY(p.porcentaje)}`).join(' ');

    return (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#ffffff' }}>
            {titulo && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                        {titulo}
                    </Typography>
                    {subtitulo && (
                        <Typography variant="caption" color="text.secondary">
                            {subtitulo}
                        </Typography>
                    )}
                </Box>
            )}

            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <svg
                    viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                    style={{ width: '100%', height: 'auto', minWidth: 600, display: 'block' }}
                >
                    <defs>
                        {/* Gradiente para área bajo la curva real */}
                        <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#1976d2" stopOpacity="0.25" />
                            <stop offset="100%" stopColor="#1976d2" stopOpacity="0.0" />
                        </linearGradient>
                    </defs>

                    {/* Zona de Éxito / Dominio Clínico (80% a 100%) */}
                    <rect
                        x={padLeft}
                        y={getY(100)}
                        width={plotWidth}
                        height={getY(80) - getY(100)}
                        fill="#2e7d32"
                        fillOpacity="0.08"
                    />
                    <text
                        x={padLeft + plotWidth - 8}
                        y={getY(90)}
                        fill="#2e7d32"
                        fontSize="10"
                        fontWeight="bold"
                        textAnchor="end"
                    >
                        Zona de Logro Esperado (≥ 80%)
                    </text>

                    {/* Cuadrícula horizontal y etiquetas Eje Y */}
                    {marcasY.map((m) => (
                        <g key={'grid-y-' + m}>
                            <line
                                x1={padLeft}
                                y1={getY(m)}
                                x2={padLeft + plotWidth}
                                y2={getY(m)}
                                stroke="#e0e0e0"
                                strokeDasharray={m === 0 || m === 100 ? 'none' : '3,3'}
                                strokeWidth="1"
                            />
                            <text
                                x={padLeft - 10}
                                y={getY(m) + 4}
                                fill="#666"
                                fontSize="11"
                                textAnchor="end"
                            >
                                {m}%
                            </text>
                        </g>
                    ))}

                    {/* Cuadrícula vertical y marcas Eje X */}
                    {marcasX.map((m) => (
                        <g key={'grid-x-' + m}>
                            <line
                                x1={getX(m)}
                                y1={padTop}
                                x2={getX(m)}
                                y2={padTop + plotHeight}
                                stroke="#f0f0f0"
                                strokeWidth="1"
                            />
                            <text
                                x={getX(m)}
                                y={padTop + plotHeight + 18}
                                fill="#555"
                                fontSize="10.5"
                                textAnchor="middle"
                            >
                                S-{m}
                            </text>
                        </g>
                    ))}

                    {/* Títulos de Ejes */}
                    <text
                        x={padLeft + plotWidth / 2}
                        y={svgHeight - 12}
                        fill="#333"
                        fontSize="12"
                        fontWeight="bold"
                        textAnchor="middle"
                    >
                        Número Correlativo de Sesión Recibida
                    </text>
                    <text
                        transform={`rotate(-90)`}
                        x={-(padTop + plotHeight / 2)}
                        y={18}
                        fill="#333"
                        fontSize="12"
                        fontWeight="bold"
                        textAnchor="middle"
                    >
                        % de Logro Clínico Acumulado
                    </text>

                    {/* Área sombreada bajo la curva real */}
                    {puntos.length > 1 && (
                        <path
                            d={`${dCurvaReal} L ${getX(puntos[puntos.length - 1].sesionNum)} ${getY(0)} L ${getX(puntos[0].sesionNum)} ${getY(0)} Z`}
                            fill="url(#areaGradient)"
                        />
                    )}

                    {/* Línea de Tendencia (Regresión Lineal) */}
                    {regresion.pendiente !== undefined && (
                        <line
                            x1={tendStart.x}
                            y1={tendStart.y}
                            x2={tendEnd.x}
                            y2={tendEnd.y}
                            stroke="#5b2d8e"
                            strokeWidth="2.5"
                            strokeDasharray="6,4"
                        />
                    )}

                    {/* Línea de Avance Real conectando los puntos */}
                    {puntos.length > 1 && (
                        <path
                            d={dCurvaReal}
                            fill="none"
                            stroke="#1976d2"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    )}

                    {/* Puntos de Dispersión (Sesiones Evaluadas) */}
                    {puntos.map((p) => {
                        const cx = getX(p.sesionNum);
                        const cy = getY(p.porcentaje);
                        const esHover = puntoActivo?.sesionNum === p.sesionNum;

                        return (
                            <g
                                key={'punto-' + p.sesionNum}
                                onMouseEnter={() => setPuntoActivo(p)}
                                onMouseLeave={() => setPuntoActivo(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                <circle
                                    cx={cx}
                                    cy={cy}
                                    r={esHover ? 7 : 5}
                                    fill={esHover ? '#e07a1f' : '#1976d2'}
                                    stroke="#ffffff"
                                    strokeWidth={esHover ? 2.5 : 1.5}
                                />
                                {esHover && (
                                    <circle
                                        cx={cx}
                                        cy={cy}
                                        r="12"
                                        fill="#e07a1f"
                                        fillOpacity="0.2"
                                    />
                                )}
                            </g>
                        );
                    })}
                </svg>
            </Box>

            {/* DETALLE FLOTANTE DEL PUNTO ACTIVO */}
            {puntoActivo ? (
                <Box
                    sx={{
                        mt: 1,
                        p: 1.2,
                        bgcolor: '#e3f2fd',
                        borderRadius: 1,
                        border: '1px solid #90caf9',
                        display: 'flex',
                        justifyContent: 'space-around',
                        flexWrap: 'wrap',
                        gap: 1
                    }}
                >
                    <Typography variant="body2">
                        <strong>Sesión N°:</strong> {puntoActivo.sesionNum}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Fecha:</strong> {puntoActivo.fechaStr}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Logro Obtenido:</strong>{' '}
                        <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                            {puntoActivo.porcentaje.toFixed(1)}%
                        </span>
                    </Typography>
                    <Typography variant="body2">
                        <strong>Valor Tendencia:</strong>{' '}
                        <span style={{ color: '#5b2d8e', fontWeight: 'bold' }}>
                            {puntoActivo.porcentajeTendencia?.toFixed(1)}%
                        </span>
                    </Typography>
                </Box>
            ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                    Pase el cursor sobre cualquier punto del gráfico para ver el detalle de la sesión.
                </Typography>
            )}

            {/* LEYENDA DEL GRÁFICO */}
            <Stack
                direction="row"
                spacing={3}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 2, pt: 1, borderTop: '1px solid #eee' }}
            >
                <Stack direction="row" spacing={0.8} alignItems="center">
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1976d2' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                        Avance Real en Sesión
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.8} alignItems="center">
                    <Box sx={{ width: 22, height: 3, bgcolor: '#5b2d8e', borderTop: '2px dashed #5b2d8e' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                        Curva de Eficacia (Regresión Lineal)
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.8} alignItems="center">
                    <Box sx={{ width: 14, height: 10, bgcolor: '#2e7d32', opacity: 0.3 }} />
                    <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                        Objetivo de Éxito (≥ 80%)
                    </Typography>
                </Stack>
            </Stack>
        </Paper>
    );
};

export default GraficoCurvaEficacia;

